import React, {useRef, useState} from "react";
import {Alert, Box, Button, DialogActions, DialogContent, Step, StepButton, Stepper} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Call from "../../hocs/call";
import ConfirmationDialog from "../confirmation-dialog";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import {STEP_COLUMN, STEP_FILE, STEP_TYPE} from "./constants";
import EtlWizardCsvFormatDialog from "./EtlWizardCsvFormatDialog";
import EtlWizardReportDialog from "./EtlWizardReportDialog";
import EtlWizardStepColumn from "./EtlWizardStepColumn";
import EtlWizardStepFile from "./EtlWizardStepFile";
import EtlWizardStepType from "./EtlWizardStepType";
import {nodeSelector} from "../../state/node/nodeSelectors";
import {
  getEtlWizardCodelist,
  getEtlWizardStructure,
  hideEtlWizardModal,
  setEasyLoadingDataset,
  setEasyLoadingResource,
  setEtlWizardColumns,
  setEtlWizardErrorMessage,
  setEtlWizardStep,
  submitEasyLoading,
  submitEtlWizard
} from "../../state/package-manager/packageManagerActions";
import {
  easyLoadingSelector,
  etlSelector,
  packageManagerSelector
} from "../../state/package-manager/packageManagerSelectors";
import {isCoordinates, isMeasure, isTimeColumnWithFormat, serializeColumnDefinitions} from "./utils";
import "./style.css";

const stepSx = {
  padding: "4px 16px"
};

const EtlWizard = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const fileStepRef = useRef();

  const node = useSelector(nodeSelector);
  const {nodeId, packages, packageId, resourceId} = useSelector(packageManagerSelector);
  const {
    isVisible,
    isEditMode,
    step,
    columns,
    enableFileUpload,
    codelist,
    errorMessage,
    packageTitle,
    csvFormat,
    isCsvFormatDialogOpen
  } = useSelector(etlSelector);
  const {dataset, resource} = useSelector(easyLoadingSelector);

  const setStep = step => dispatch(setEtlWizardStep(step));
  const setDataset = dataset => dispatch(setEasyLoadingDataset(dataset));
  const setResource = resource => dispatch(setEasyLoadingResource(resource));
  const startEasyLoading = () => dispatch(submitEasyLoading(nodeId, node.type, dataset, resource));
  const setColumns = columns => dispatch(setEtlWizardColumns(columns));
  const setErrorMessage = errorMessage => dispatch(setEtlWizardErrorMessage(errorMessage));

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const onWizardDialogClose = () => {
    dispatch(hideEtlWizardModal());
  };

  const onDataStructureFetch = ({nodeId, resourceId, isEditMode}) =>
    dispatch(getEtlWizardStructure(nodeId, resourceId, isEditMode));

  const onDataImportSubmit = (columns, forceImport) => {
    let title = "";
    if (dataset) {
      title = dataset.title || "";
    } else if (packages) {
      title = (packages || []).find(pkg => pkg.id === packageId)?.title || "";
    } else {
      title = packageTitle || "";
    }
    const etlResource = {
      title: title,
      columnDefinitions: serializeColumnDefinitions(columns),
      csvFormat: csvFormat
    };
    dispatch(submitEtlWizard(etlResource, nodeId, packageId, resourceId, forceImport));
  };

  const onCodelistFetch = ({nodeId}) => dispatch(getEtlWizardCodelist(nodeId));

  const handleGoToStep = nextStep => {
    if (step === STEP_FILE && nextStep === STEP_COLUMN) {
      if (!resource || !resource.content) {
        setErrorMessage(t("modules.packageManager.etlWizard.errors.invalidFile"));
        return;
      }

      if (!dataset || !dataset.title || !dataset.theme || !dataset.description || !dataset.organization) {
        setDataset({...dataset, hasError: true});
        if (!dataset.description || !dataset.organization) fileStepRef.current.setExpanded(true);
        return;
      }

      // Clear the error state once the issues have been resolved
      if (errorMessage) setErrorMessage(null);
      if (dataset.hasError) setDataset({...dataset, hasError: false});

      if (!packageId) {
        startEasyLoading();
      } else {
        setStep(nextStep);
      }
    } else if (step === STEP_COLUMN && nextStep === STEP_TYPE) {
      // Keep only the items with import: true
      const selectedColumns = columns.filter(column => column.import);

      // Check whether no column has been selected
      if (selectedColumns.length === 0) {
        setErrorMessage(t("modules.packageManager.etlWizard.errors.noColumnsSelected"));
        return;
      }

      // Check that each selected column has a label
      const allLabelsPresent = selectedColumns.every(item => !!item.alias?.trim());
      if (!allLabelsPresent) {
        setErrorMessage(t("modules.packageManager.etlWizard.errors.missingLabels"));
        setColumns(columns.map(column => ({...column, hasError: !column.alias?.trim()})));
        return;
      }

      // Check whether there are columns with duplicate labels
      const uniqueLabels = [];
      const duplicatedColumns = [];
      selectedColumns.forEach(column => {
        if (uniqueLabels.includes(column.alias)) {
          duplicatedColumns.push(column.name);
        } else {
          uniqueLabels.push(column.alias);
        }
      });
      if (duplicatedColumns.length > 0) {
        setErrorMessage(t("modules.packageManager.etlWizard.errors.duplicateLabels"));
        setColumns(columns.map(column => ({...column, hasError: duplicatedColumns.includes(column.name)})));
        return;
      }

      // If there was an error message and the issues are now fixed, clear the
      // error message and any existing errors on the resource and columns
      if (errorMessage) {
        setErrorMessage(null);
        setColumns(columns.map(column => ({...column, hasError: false})));
      }

      // Sort the columns so they are displayed in the established order
      setColumns([...columns].sort((a, b) => a.position - b.position));
      setStep(nextStep);
    } else {
      setErrorMessage(null);
      setColumns(columns.map(column => ({...column, hasError: false})));
      setStep(nextStep);
    }
  };

  // Step Type
  const validateColumn = column => {
    let hasError = false;
    if (isMeasure(column) && !column.decimalSeparator) hasError = true;
    if (isTimeColumnWithFormat(column) && !column.formatDate) hasError = true;
    if (isCoordinates(column) && !column.formatCoordinates) hasError = true;
    return hasError;
  };

  const handleSubmit = () => {
    const validatedColumns = columns.map(column => ({...column, hasError: validateColumn(column)}));
    if (validatedColumns.some(column => column.hasError)) {
      setErrorMessage(t("modules.packageManager.etlWizard.errors.genericType"));
      setColumns(validatedColumns);
    } else {
      setErrorMessage(null);
      setConfirmDialogOpen(true);
    }
  };

  return (
    <>
      <CustomDialog className="etl-wizard" open={isVisible} onClose={onWizardDialogClose}>
        <CustomDialogTitle className="etl-wizard__dialog-title" onClose={onWizardDialogClose}>
          {enableFileUpload
            ? t("modules.packageManager.etlWizard.dialog.title.withFileUpload")
            : t("modules.packageManager.etlWizard.dialog.title.withoutFileUpload")}
        </CustomDialogTitle>
        <DialogContent className="etl-wizard__dialog-content">
          <Box className="etl-wizard__dialog-content__etl-container" sx={{height: "100%", minWidth: "900px"}}>
            <Stepper alternativeLabel activeStep={step}>
              {enableFileUpload && (
                <Step active={step === STEP_FILE} sx={stepSx}>
                  <StepButton onClick={() => handleGoToStep(STEP_FILE)}>
                    {t("modules.packageManager.etlWizard.steps.fileUpload")}
                  </StepButton>
                </Step>
              )}
              <Step active={step === STEP_COLUMN} sx={stepSx}>
                <StepButton onClick={() => handleGoToStep(STEP_COLUMN)}>
                  {t("modules.packageManager.etlWizard.steps.columnSelection")}
                </StepButton>
              </Step>
              <Step active={step === STEP_TYPE} sx={stepSx}>
                <StepButton disabled={step === STEP_FILE} onClick={() => handleGoToStep(STEP_TYPE)}>
                  {t("modules.packageManager.etlWizard.steps.columnType")}
                </StepButton>
              </Step>
            </Stepper>
            {errorMessage && (
              <Alert className="etl-wizard__alerts__error" severity="error">
                {errorMessage}
              </Alert>
            )}
            {step === STEP_FILE && (
              <EtlWizardStepFile
                ref={fileStepRef}
                nodeId={nodeId}
                dataset={dataset}
                setDataset={setDataset}
                resource={resource}
                setResource={setResource}
                errorMessage={errorMessage}
                disabled={!!packageId}
              />
            )}
            {step === STEP_COLUMN && (
              <Call
                cb={onDataStructureFetch}
                cbParam={{nodeId, resourceId, isEditMode: isEditMode}}
                disabled={!resourceId || (columns || [])?.length > 0}
              >
                <EtlWizardStepColumn columns={columns} setColumns={setColumns} errorMessage={errorMessage} />
              </Call>
            )}
            {step === STEP_TYPE && (
              <Call cb={onCodelistFetch} cbParam={{nodeId}} disabled={codelist}>
                <EtlWizardStepType
                  columns={columns}
                  setColumns={setColumns}
                  validate={validateColumn}
                  errorMessage={errorMessage}
                />
              </Call>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {(step === STEP_TYPE || (step === STEP_COLUMN && enableFileUpload)) && (
            <Button onClick={() => handleGoToStep(step === STEP_TYPE ? STEP_COLUMN : STEP_FILE)}>
              {t("commons.confirm.back")}
            </Button>
          )}
          {(step === STEP_FILE || step === STEP_COLUMN) && (
            <Button onClick={() => handleGoToStep(step === STEP_FILE ? STEP_COLUMN : STEP_TYPE)}>
              {t("commons.confirm.next")}
            </Button>
          )}
          {step === STEP_TYPE && (
            <>
              <Button onClick={handleSubmit}>{t("commons.confirm.next")}</Button>
              <ConfirmationDialog
                title={t("modules.packageManager.etlWizard.import.dialog.confirm.title")}
                content={t("modules.packageManager.etlWizard.import.dialog.confirm.description")}
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={() => {
                  onDataImportSubmit(columns);
                  setConfirmDialogOpen(false);
                }}
              />
            </>
          )}
        </DialogActions>
      </CustomDialog>

      <EtlWizardCsvFormatDialog
        open={isCsvFormatDialogOpen}
        csvFormat={csvFormat}
        onClose={onWizardDialogClose}
        onConfirm={() => {
          dispatch(getEtlWizardStructure(nodeId, resourceId, isEditMode, csvFormat));
        }}
      />

      <EtlWizardReportDialog columns={columns} onDataImportSubmit={onDataImportSubmit} />
    </>
  );
};

export default EtlWizard;
